import { useEffect, useRef, useState } from "react";

const LENGTH = 4;

// Four single-digit boxes. Typing moves forward, Backspace moves back, and
// pasting (or the phone's "from Mail" suggestion) fills every box at once.
// onComplete fires once all four digits are in.
export default function OtpInput({ onComplete, busy, labelledBy, describedBy }) {
  const [digits, setDigits] = useState(Array(LENGTH).fill(""));
  const refs = useRef([]);

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  const fill = (next) => {
    setDigits(next);
    if (next.every(Boolean)) onComplete(next.join(""));
  };

  const spread = (text, from) => {
    const incoming = text.replace(/\D/g, "").slice(0, LENGTH - from).split("");
    if (!incoming.length) return;
    const next = [...digits];
    incoming.forEach((d, i) => {
      next[from + i] = d;
    });
    fill(next);
    refs.current[Math.min(from + incoming.length, LENGTH - 1)]?.focus();
  };

  const onChange = (index, raw) => {
    // Box 1 accepts a whole pasted or autofilled code, so typing into it
    // when it already holds a digit must keep only the new keystroke.
    const value =
      digits[index] && raw.length === digits[index].length + 1 && raw.startsWith(digits[index])
        ? raw.slice(-1)
        : raw;
    if (value.length > 1) return spread(value, index);
    if (value && !/^\d$/.test(value)) return undefined;
    const next = [...digits];
    next[index] = value;
    fill(next);
    if (value && index < LENGTH - 1) refs.current[index + 1]?.focus();
    return undefined;
  };

  const onKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="cr-otp-boxes" role="group" aria-labelledby={labelledBy} aria-describedby={describedBy}>
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          className="cr-otp-box"
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={i === 0 ? LENGTH : 1}
          value={digit}
          readOnly={busy}
          aria-label={`Digit ${i + 1} of ${LENGTH}`}
          onChange={(e) => onChange(i, e.target.value)}
          onKeyDown={(e) => onKeyDown(i, e)}
          onPaste={(e) => {
            e.preventDefault();
            spread(e.clipboardData.getData("text"), i);
          }}
        />
      ))}
    </div>
  );
}
